import type { SupportedDatePattern } from '@config/localizationConfig';
import { formatDate } from '@utils/dateFormat';

function getFormatter(
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone,
  });
}

function getZonedDateTimeParts(date: Date, timeZone: string) {
  const parts = getFormatter('en-CA', timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

export function convertBuildingLocalDateTimeToUtcIso(
  date: string,
  time: string | undefined,
  timeZone?: string
): string {
  if (!date) return '';

  const dateOnly = date.slice(0, 10);
  const [yearPart, monthPart, dayPart] = dateOnly.split('-').map(Number);
  const [hourPart, minutePart] = (time ?? '00:00').split(':').map(Number);
  const year = yearPart ?? Number.NaN;
  const month = monthPart ?? Number.NaN;
  const day = dayPart ?? Number.NaN;
  const hours = hourPart ?? Number.NaN;
  const minutes = minutePart ?? Number.NaN;

  if (
    [year, month, day, hours, minutes].some((value) => Number.isNaN(value) || value === undefined)
  ) {
    return `${dateOnly}T${time ?? '00:00'}:00Z`;
  }

  if (!timeZone) {
    return `${dateOnly}T${time ?? '00:00'}:00Z`;
  }

  const targetLocalKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;

  let utcMs = Date.UTC(year, month - 1, day, hours, minutes, 0);

  for (let index = 0; index < 6; index += 1) {
    const zoned = getZonedDateTimeParts(new Date(utcMs), timeZone);
    const zonedKey = `${zoned.year}-${String(zoned.month).padStart(2, '0')}-${String(zoned.day).padStart(2, '0')} ${String(zoned.hour).padStart(2, '0')}:${String(zoned.minute).padStart(2, '0')}:${String(zoned.second).padStart(2, '0')}`;

    if (zonedKey === targetLocalKey) {
      return new Date(utcMs).toISOString().replace('.000Z', 'Z');
    }

    const targetComparable = Date.UTC(year, month - 1, day, hours, minutes, 0);
    const zonedComparable = Date.UTC(
      zoned.year,
      zoned.month - 1,
      zoned.day,
      zoned.hour,
      zoned.minute,
      zoned.second
    );
    utcMs += targetComparable - zonedComparable;
  }

  return new Date(utcMs).toISOString().replace('.000Z', 'Z');
}

export function formatUtcDateInTimeZone(
  isoUtc: string | undefined,
  timeZone: string | undefined,
  pattern?: SupportedDatePattern
): string {
  if (!isoUtc) return '';

  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return isoUtc;

  // Resolve the calendar Y/M/D in the target timezone, then reconstruct a
  // local-tz Date carrying that calendar date so it can be routed through the
  // central pattern formatter. Only the calendar date matters here.
  let year: number;
  let month: number;
  let day: number;

  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    }).formatToParts(date);
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    year = Number(map.year);
    month = Number(map.month);
    day = Number(map.day);
  } else {
    year = date.getFullYear();
    month = date.getMonth() + 1;
    day = date.getDate();
  }

  if ([year, month, day].some((v) => Number.isNaN(v))) {
    return isoUtc;
  }

  const reconstructed = new Date(year, month - 1, day);
  return formatDate(reconstructed, pattern);
}

export function formatUtcTimeInTimeZone(
  value: string,
  locale: string,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions
): string {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  if (!timeZone) {
    return date.toLocaleTimeString(locale, options);
  }

  return getFormatter(locale, timeZone, options).format(date);
}

/**
 * Returns the calendar date in `YYYY-MM-DD` form for use with HTML
 * `<input type="date">` controls (which require an ISO 8601 date literal).
 *
 * This is the only date-related helper that intentionally bypasses the
 * central pattern configuration: the HTML5 spec mandates this exact format
 * for `type="date"` value binding, regardless of the user's display locale.
 *
 * @param isoUtc - UTC ISO 8601 instant.
 * @param timeZone - IANA time zone for calendar-date resolution. When undefined,
 *                   the runtime/local zone is used.
 * @returns `YYYY-MM-DD` string, or `''` for missing/invalid inputs.
 */
export function getIsoDateInTimeZone(isoUtc: string | undefined, timeZone?: string): string {
  if (!isoUtc) return '';

  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return '';

  let year: number;
  let month: number;
  let day: number;

  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    }).formatToParts(date);
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    year = Number(map.year);
    month = Number(map.month);
    day = Number(map.day);
  } else {
    year = date.getFullYear();
    month = date.getMonth() + 1;
    day = date.getDate();
  }

  if ([year, month, day].some((v) => Number.isNaN(v))) return '';

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function formatLocalDateForStorage(date?: Date | null): string | null {
  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function parseStoredDateToLocalDate(value?: string | null): Date | null {
  if (!value) return null;

  const dateOnly = value.slice(0, 10);
  const [yearPart, monthPart, dayPart] = dateOnly.split('-').map(Number);
  const year = yearPart ?? Number.NaN;
  const month = monthPart ?? Number.NaN;
  const day = dayPart ?? Number.NaN;

  if ([year, month, day].some((part) => Number.isNaN(part))) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return new Date(year, month - 1, day);
}

/**
 * Checks whether the No Occurrence action window is currently open.
 *
 * The window mirrors the backend's `BuildingTimeZoneHelper.LocalMidnightAfterToUtc`:
 *   windowStart = midnight of (eventEndDate.date + 1 day) in building timezone, converted to UTC
 *   windowEnd   = windowStart + 24 hours
 *
 * @param eventEndDateIso - UTC ISO 8601 string of the event end date/time.
 * @param buildingTimeZone - IANA timezone of the building (e.g. "America/Los_Angeles").
 *                           Falls back to UTC when undefined.
 * @param now - Optional override for current time (for testing). Defaults to Date.now().
 * @returns `true` if the current time is within the 24-hour window.
 */
export function isNoOccurrenceWindowOpen(
  eventEndDateIso: string | undefined,
  buildingTimeZone: string | undefined,
  now?: Date
): boolean {
  if (!eventEndDateIso) return false;

  const eventEndDate = new Date(eventEndDateIso);
  if (Number.isNaN(eventEndDate.getTime())) return false;

  const tz = buildingTimeZone ?? 'UTC';

  // Step 1: Get the calendar date of the event end in building timezone
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: tz,
  }).formatToParts(eventEndDate);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const year = Number(map.year);
  const month = Number(map.month);
  const day = Number(map.day);

  if ([year, month, day].some((v) => Number.isNaN(v))) return false;

  // Step 2: Midnight of the NEXT day in building timezone → UTC
  // Build "YYYY-MM-DDT00:00:00" for the next day, convert via building tz to UTC
  const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
  const nextDayStr = `${nextDay.getUTCFullYear()}-${String(nextDay.getUTCMonth() + 1).padStart(2, '0')}-${String(nextDay.getUTCDate()).padStart(2, '0')}`;
  const windowStartUtcIso = convertBuildingLocalDateTimeToUtcIso(nextDayStr, '00:00', tz);
  const windowStart = new Date(windowStartUtcIso);
  if (Number.isNaN(windowStart.getTime())) return false;

  // Step 3: Window is 24 hours
  const windowEnd = new Date(windowStart.getTime() + 24 * 60 * 60 * 1000);

  const currentTime = now ?? new Date();
  return currentTime >= windowStart && currentTime <= windowEnd;
}
